import React from "react";
import styled from "styled-components";
import {
  colors2,
  TextInput,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_BODY_SM_DF,
  UI_CAPTION,
  UI_HEADLINE,
} from "oolib";
import { ButtonPrimary } from "oolib/dist/v2/components/Buttons";

// ============================================
// SHARED STYLED COMPONENTS
// These can be imported and used by custom integration interfaces
// ============================================

export const InterfaceContainer = styled.div`
  padding: 20px;
  border: 1px solid ${colors2.grey10};
  border-radius: 8px;
  background: ${colors2.grey5};
`;

export const InterfaceHeader = styled(UI_BODY_SEMIBOLD_SM_DF)`
  margin-bottom: 12px;
`;

export const InterfaceDescription = styled(UI_BODY_SM_DF)`
  margin-bottom: 16px;
  color: ${colors2.grey60};
`;

export const InterfaceActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;

export const HelpText = styled(UI_CAPTION)`
  color: ${colors2.grey60};
  margin-top: 8px;

  a {
    color: ${colors2.blue};
    text-decoration: underline;
  }
`;

// Icon container for tile display
export const TileIconContainer = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: ${colors2.grey5};

  img {
    width: 32px;
    height: 32px;
    object-fit: contain;
  }

  svg {
    width: 32px;
    height: 32px;
    color: ${colors2.grey60};
  }
`;

// ============================================
// OAUTH CONNECTION BANNERS
// Shared across every framework-OAuth integration's connected/stale/error
// render states. Live here so each integration file doesn't redefine them.
// ============================================

export const ConnectedBanner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-top: 16px;
  border: 1px solid ${colors2.grey20 || "#e6e6e6"};
  border-radius: 8px;
  background: #f4faff;
`;

export const ConnectedMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 14px;
`;

export const StaleBanner = styled(ConnectedBanner)`
  background: #fff7e6;
  border-color: #ffd97a;
`;

export const ErrorBanner = styled.div`
  margin-top: 16px;
  padding: 10px 14px;
  color: #a21b2b;
  background: #fdecea;
  border: 1px solid #f5c2c2;
  border-radius: 6px;
  font-size: 14px;
`;

export const EmptyBanner = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  color: #5a4a16;
  background: #fff8e1;
  border: 1px solid #f0d97d;
  border-radius: 6px;
  font-size: 14px;
`;

/**
 * DefaultIconFallback
 * Shows the first letter of the integration name when no icon is available
 */
export const DefaultIconFallback = ({ name }) => (
  <UI_HEADLINE style={{ color: colors2.grey60 }}>
    {name?.charAt(0)?.toUpperCase() || "?"}
  </UI_HEADLINE>
);

/**
 * BaseIntegrationInterface
 *
 * Base component that all integration interfaces should extend or use as reference.
 * Provides the standard API key input pattern that most integrations will use.
 *
 * For simple API key integrations, you can use this directly.
 * For complex integrations (like Slack with channel selection), create a custom component
 * that follows the same interface contract.
 *
 * ============================================
 * INTERFACE CONTRACT FOR CUSTOM INTEGRATIONS:
 * ============================================
 *
 * Each integration interface must export:
 *
 * 1. IntegrationInterface (Component) - The main interface component
 *    Props:
 *    - adapterConfig: Object - Config from EXTERNAL_IMPORT_ADAPTERS_CONFIG
 *    - onSubmit: Function({ apiKey, adapterOptions }) - Called when user submits
 *    - isLoading: boolean - Shows loading state
 *
 * 2. IntegrationIcon (Component) - The icon to show in the tile
 *    Props:
 *    - size: number (optional, default 32) - Icon size in pixels
 *
 *    Can be:
 *    - An SVG component
 *    - An img tag with imported asset
 *    - DefaultIconFallback for text-based fallback
 *
 * 3. integrationMeta (Object) - Metadata about the integration
 *    - id: string - Must match the adapter id in backend config
 *    - name: string - Display name
 *    - description: string - Short description
 *
 * ============================================
 */

// Props interface for reference:
// @param {Object} adapterConfig - The adapter config from EXTERNAL_IMPORT_ADAPTERS_CONFIG
// @param {Function} onSubmit - Called with { apiKey, adapterOptions } when user submits
// @param {boolean} isLoading - Shows loading state on submit button

export const BaseIntegrationInterface = ({
  adapterConfig,
  onSubmit,
  isLoading,
}) => {
  const [apiKey, setApiKey] = React.useState("");

  const handleSubmit = () => {
    if (!apiKey.trim()) return;
    onSubmit({
      apiKey: apiKey.trim(),
      adapterOptions: {},
    });
  };

  return (
    <InterfaceContainer>
      <InterfaceHeader>
        Connect to {adapterConfig.name}
      </InterfaceHeader>
      <InterfaceDescription>
        {adapterConfig.description}
      </InterfaceDescription>

      <TextInput
        label={adapterConfig.authConfig?.keyLabel || "API Key"}
        placeholder={
          adapterConfig.authConfig?.keyPlaceholder || "Enter your API key"
        }
        value={apiKey}
        onChange={(k, v) => setApiKey(v)}
        type="password"
      />

      {adapterConfig.authConfig?.helpText && (
        <HelpText>
          {adapterConfig.authConfig.helpText}
          {adapterConfig.authConfig?.helpUrl && (
            <>
              {" "}
              <a
                href={adapterConfig.authConfig.helpUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </>
          )}
        </HelpText>
      )}

      <InterfaceActions>
        <ButtonPrimary
          onClick={handleSubmit}
          disabled={!apiKey.trim() || isLoading}
          isLoading={isLoading}
        >
          Fetch Data
        </ButtonPrimary>
      </InterfaceActions>
    </InterfaceContainer>
  );
};

// Default icon - just shows first letter
export const BaseIntegrationIcon = ({ name, size = 32 }) => (
  <DefaultIconFallback name={name} />
);

// Default meta - should be overridden by each integration
export const baseIntegrationMeta = {
  id: "base",
  name: "Base Integration",
  description: "Base integration interface",
};

export default BaseIntegrationInterface;
